import { VStack } from "@chakra-ui/react";
import { Form, Formik } from "formik";
import { FC, useCallback, useMemo, useState } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { useParams } from "react-router-dom";
import * as yup from "yup";

import { GraphQLEnums } from "~/src/__generated__/GraphQLEnums";
import { PopupPageReportFilterButtonForm_Query } from "~/src/__relay_artifacts__/PopupPageReportFilterButtonForm_Query.graphql";
import { FormControl } from "~/src/components/common/forms/FormControl";
import { FormikFormButtons } from "~/src/components/common/forms/FormikFormButtons";
import { FormikMultipleSelectField } from "~/src/components/common/forms/FormikMultipleSelectField";
import { FormikSelectField } from "~/src/components/common/forms/FormikSelectField";

import { PopupReportFilterFormValues as FormValues } from "./types";

export type Props = {
  filterValues: FormValues;
  onSubmit: (values: FormValues) => Promise<void> | void;
  onClose: () => void;
};

const query = graphql`
  query PopupPageReportFilterButtonForm_Query(
    $slug: String!
    $pageKind: PageKind
    $pageIds: [ID!]
    $funnelIds: [ID!]
  ) {
    sources {
      edges {
        node {
          id
          name
        }
      }
    }
    site(slug: $slug) {
      funnels {
        edges {
          node {
            id
            name
          }
        }
      }
      popups(pageIds: $pageIds, pageKind: $pageKind, funnelIds: $funnelIds) {
        edges {
          node {
            id
            name
            page {
              id
              kind
              name
              sortNumber
              funnel {
                id
                name
              }
            }
          }
        }
      }
      pages(funnelIds: $funnelIds, pageKind: $pageKind) {
        edges {
          node {
            id
            kind
            name
            sortNumber
            funnel {
              id
              name
            }
          }
        }
      }
    }
  }
`;

const validationSchema = yup.object({
  sourceId: yup.string().nullable(),
  device: yup.string().nullable(),
  funnelIds: yup.array().of(yup.string()).nullable(),
  pageIds: yup.array().of(yup.string()).nullable(),
  pageKind: yup.string().nullable(),
  popupIds: yup.array().of(yup.string()).nullable(),
});

export const PopupPageReportFilterButtonForm: FC<
  React.PropsWithChildren<React.PropsWithChildren<Props>>
> = ({ filterValues, onSubmit, onClose }) => {
  const { siteSlug = "" } = useParams();
  const [funnelValues, setFunnelValues] = useState(filterValues.funnelIds);
  const [pageKindValue, setPageKindValue] = useState(filterValues.pageKind);
  const [popupValues, setPopupValues] = useState(filterValues.popupIds);
  const [pageValues, setPageValues] = useState(filterValues.pageIds);

  const data = useLazyLoadQuery<PopupPageReportFilterButtonForm_Query>(query, {
    slug: siteSlug,
    funnelIds: funnelValues,
    pageIds: pageValues,
    pageKind: pageKindValue,
  });

  const sourceOptions = useMemo(
    () => [
      { label: "すべて", value: null },
      ...(data.sources.edges || []).map((edge) => {
        const node = edge?.node;
        if (!node) throw new Error("assertion failed");
        return { label: node.name, value: node.id };
      }),
    ],
    [data.sources.edges]
  );

  const funnelOptions = useMemo(() => {
    return (
      data.site.funnels?.edges?.map((edge) => {
        const node = edge?.node;
        if (!node) throw new Error("assertion failed");
        return { label: node.name, value: node.id };
      }) || []
    );
  }, [data.site.funnels]);

  const pageKindOptions = useMemo(
    () => [{ label: "すべて", value: null }, ...GraphQLEnums.PageKind],
    []
  );

  const deviceOptions = useMemo(
    () => [{ label: "すべて", value: null }, ...GraphQLEnums.Device],
    []
  );

  const pageOptions = useMemo(() => {
    return [
      ...(data.site.pages.edges || []).map((edge) => {
        const node = edge?.node;
        if (!node) throw new Error("assertion failed");

        const pageName =
          node.kind === "FORM"
            ? `${node.name}${(node.sortNumber - 1).toString()}`
            : node.name;
        return {
          label: `${pageName}(${node.funnel.name})`,
          value: node.id,
          funnelId: node.funnel.id,
          pageKind: node.kind,
        };
      }),
    ];
  }, [data.site.pages]);

  const popupOptions = useMemo(() => {
    return [
      ...(data.site.popups.edges || []).map((edge) => {
        const node = edge?.node;
        if (!node) throw new Error("assertion failed");

        const pageName =
          node.page.kind === "FORM"
            ? `${node.page.name}(${(node.page.sortNumber - 1).toString()})`
            : node.page.name;
        return {
          label: `${node.name || ""}(${node.page.funnel.name})[${pageName}]`,
          value: node.id,
          funnelId: node.page.funnel.id,
          pageKind: node.page.kind,
          pageId: node.page.id,
        };
      }),
    ];
  }, [data.site.popups]);

  const handleSubmit = useCallback(
    (values: FormValues) => {
      onSubmit(values);
      onClose();
    },
    [onClose, onSubmit]
  );

  return (
    <Formik<FormValues>
      enableReinitialize
      initialValues={filterValues}
      validationSchema={validationSchema}
      validateOnMount={false}
      validateOnChange={false}
      onSubmit={handleSubmit}
    >
      <Form>
        <VStack spacing="16px">
          <FormControl label="ファネル">
            <FormikMultipleSelectField
              name="funnelIds"
              options={funnelOptions}
              onSetStateAction={setFunnelValues}
              isDisabled={popupValues.length > 0 || pageValues.length > 0}
            />
          </FormControl>
          <FormControl label="ページタイプ">
            <FormikSelectField
              name="pageKind"
              options={pageKindOptions}
              onSetStateAction={setPageKindValue}
              isDisabled={popupValues.length > 0 || pageValues.length > 0}
            />
          </FormControl>
          <FormControl label="ページ">
            <FormikMultipleSelectField
              name="pageIds"
              options={pageOptions}
              onSetStateAction={setPageValues}
              isDisabled={popupValues.length > 0}
            />
          </FormControl>
          <FormControl label="ポップアップ">
            <FormikMultipleSelectField
              name="popupIds"
              options={popupOptions}
              onSetStateAction={setPopupValues}
            />
          </FormControl>
          <FormControl label="流入元">
            <FormikSelectField name="sourceId" options={sourceOptions} />
          </FormControl>
          <FormControl label="デバイス">
            <FormikSelectField name="device" options={deviceOptions} />
          </FormControl>
        </VStack>
        <FormikFormButtons onCancelClick={onClose} />
      </Form>
    </Formik>
  );
};
